import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, XCircle, Crown, ArrowRight } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("failed");
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (error) {
        console.error("Payment status check failed, retrying:", error);
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, checkAuth]);

  return (
    <div className="grid-bg min-h-[80vh] flex items-center justify-center px-5">
      <div className="glass rounded-2xl p-12 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <div className="w-14 h-14 border-2 border-[#FFEB3B] border-t-transparent rounded-full animate-spin mx-auto mb-8" />
            <h1 className="font-display font-black text-2xl uppercase tracking-tight">Confirming payment…</h1>
            <p className="text-white/50 mt-4">Hang tight, cueing the applause.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle weight="fill" size={72} className="text-[#FFEB3B] mx-auto mb-6" />
            <div className="flex items-center justify-center gap-2 text-[#FFEB3B] mb-3">
              <Crown weight="fill" size={20} />
              <span className="font-bold uppercase tracking-widest text-sm">Premium Unlocked</span>
            </div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tight">You're in!</h1>
            <p className="text-white/60 mt-4">Unlimited downloads are now yours. Go wild.</p>
            <Link to="/marketplace" data-testid="success-browse-btn"
              className="inline-flex items-center gap-2 bg-[#FFEB3B] text-[#05050A] font-bold px-6 py-3.5 rounded-sm mt-8 hover:bg-[#fff35c] transition-colors">
              Browse packs <ArrowRight weight="bold" size={16} />
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle weight="fill" size={72} className="text-[#FF00AA] mx-auto mb-6" />
            <h1 className="font-display font-black text-2xl uppercase tracking-tight">Payment not confirmed</h1>
            <p className="text-white/60 mt-4">We couldn't confirm your payment. If you were charged, it may take a moment.</p>
            <Link to="/premium" className="inline-flex items-center gap-2 glass border border-white/20 px-6 py-3.5 rounded-sm mt-8 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
              Back to plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
